import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { lkInvites, ApiError } from "@flame/shared/api";
import { Button, Input, toast } from "@flame/shared/ui";
import { formatDate } from "@flame/shared/lib";

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  code: z
    .string()
    .min(4, "Код не менее 4 символов")
    .max(32, "Код не более 32 символов"),
});

type FormData = z.infer<typeof schema>;

// ── Error mapping ─────────────────────────────────────────────────────────────

function mapError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 404: return "Инвайт не найден";
      case 410: return "Инвайт больше недействителен";
      case 409: return "Инвайт уже использован";
      case 403: return "Аккаунт заблокирован";
      default:  return err.message || "Ошибка сервера";
    }
  }
  return "Неизвестная ошибка";
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ActivatePage() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ code }: FormData) => {
    try {
      const res = await lkInvites.redeem(code);

      const familyLabel = res.tariff_family === "black" ? "Black" : "White";
      const tierLabel   = res.tariff_tier   === "pro"   ? "Premium"   : "Basic";

      toast.success(
        `Активировано — ${familyLabel} ${tierLabel}`,
        `Действует до ${formatDate(res.expires_at)}`,
      );

      await queryClient.invalidateQueries({ queryKey: ["subs"] });
      void navigate("/", { replace: true });
    } catch (err) {
      setError("code", { message: mapError(err) });
    }
  };

  return (
    <div className="mx-auto max-w-sm">
      {/* Back link */}
      <button
        type="button"
        onClick={() => void navigate(-1)}
        className="mb-6 flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path
            d="M9 2L4 7l5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Назад
      </button>

      <div className="rounded-lg bg-surface p-6 shadow-card">
        <h2 className="mb-1 text-base font-semibold text-fg">Активировать инвайт</h2>
        <p className="mb-5 text-sm text-muted">
          Введите инвайт-код, который вам выдали
        </p>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="flex flex-col gap-4">
          <Input
            label="Код"
            placeholder="XXXX-XXXX-XXXX"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            error={errors.code?.message}
            {...register("code", {
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                setValue("code", e.target.value.toUpperCase(), {
                  shouldValidate: false,
                });
              },
            })}
          />

          <div className="flex gap-3">
            <Button
              type="button"
              variant="ghost"
              size="md"
              className="flex-1"
              onClick={() => void navigate("/")}
              disabled={isSubmitting}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="flex-1"
              loading={isSubmitting}
            >
              Активировать
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
